// public/v3/src/pages/letters.jsx — Provider Letters surface.
//
// Two views, tabbed:
//   • Draft — pin a patient, pick a template (or a one-time blank), fill any
//     manual merge fields, describe the ask, and let the assistant draft the
//     body (POST /api/provider-letters/draft). Edit, then the MANDATORY real-PDF
//     preview gate (POST /preview, side-effect-free) renders the actual letter
//     in an iframe with the send actions beneath it (app-wide preview-before-send
//     rule). "Save draft" leaves an unsigned Elation letter; "Sign & send" signs
//     + faxes/emails now.
//   • Templates — the builder: create/edit/publish/retire reusable templates.
//
// Server route: routes/providerLetters.js (provider/admin + orderchat
// permission). A 404 means the practice hasn't turned on providerLettersEnabled
// yet → the friendly dormant state.
import React, { useState, useEffect, useCallback } from 'react';
import { ccmApi } from '../lib/api.js';
import { patientFragment, applyCanonicalName, formatDob } from '../lib/order-chat-patient-pin.js';
import '../styles/order-chat.css';
import '../styles/letters.css';

// base64 PDF → object URL for the preview iframe (lab-orders parity).
function base64PdfUrl(pdfBase64) {
  try {
    const blob = new Blob([Uint8Array.from(atob(pdfBase64), (c) => c.charCodeAt(0))], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

// {errors:[...]} lines (422) → array; else the message; else fallback.
function errorLines(e, fallback) {
  const body = e && e.body;
  if (body && Array.isArray(body.errors) && body.errors.length) return body.errors;
  return [(e && e.message) || fallback];
}

// ---------------------------------------------------------------------------
// Patient pin (the Order Chat pin-picker, shared with lab-orders)
// ---------------------------------------------------------------------------
function usePatientPin(busy) {
  const [patientInput, setPatientInput] = useState('');
  const [pinnedPatient, setPinnedPatient] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);

  useEffect(() => {
    const frag = patientFragment(patientInput) || patientInput.trim();
    const pinnedName = pinnedPatient ? `${pinnedPatient.firstName} ${pinnedPatient.lastName}` : '';
    if (frag.length < 2 || frag.toLowerCase() === pinnedName.toLowerCase()) {
      setSuggestions([]); setSuggestOpen(false); return;
    }
    let live = true;
    const t = setTimeout(async () => {
      try {
        const data = await ccmApi.searchOrderChatPatients(frag);
        if (!live) return;
        const results = (data && data.results) || [];
        setSuggestions(results); setSuggestOpen(results.length > 0);
      } catch { if (live) { setSuggestions([]); setSuggestOpen(false); } }
    }, 200);
    return () => { live = false; clearTimeout(t); };
  }, [patientInput, pinnedPatient]);

  function onInput(value) {
    setPatientInput(value);
    if (pinnedPatient) setPinnedPatient(null);
  }
  function select(p) {
    setPatientInput(applyCanonicalName(patientInput, `${p.firstName} ${p.lastName}`));
    setPinnedPatient({ elationId: p.elationId, firstName: p.firstName, lastName: p.lastName, dob: p.dob });
    setSuggestions([]); setSuggestOpen(false);
  }

  const view = (
    <div className="field laborders-patient">
      <div className="field-label">Patient</div>
      <input type="text" value={patientInput} placeholder="Type a name…"
             onChange={(e) => onInput(e.target.value)} disabled={busy} />
      <ul className="orderchat-suggest" role="listbox" hidden={!suggestOpen || suggestions.length === 0}>
        {suggestions.map((p) => (
          <li key={p.directoryId} role="option" className="orderchat-suggest-item"
              onMouseDown={(e) => { e.preventDefault(); select(p); }}>
            <span className="orderchat-suggest-name">{p.firstName} {p.lastName}</span>
            {p.dob ? <span className="orderchat-suggest-dob">DOB {formatDob(p.dob)}</span> : null}
          </li>
        ))}
      </ul>
      {pinnedPatient && (
        <div className="orderchat-pin">
          <span aria-hidden="true">📌</span>
          <strong>{pinnedPatient.firstName} {pinnedPatient.lastName}</strong>
          {pinnedPatient.dob ? <span className="orderchat-pin-dob">· DOB {formatDob(pinnedPatient.dob)}</span> : null}
          <button type="button" className="orderchat-pin-x" aria-label="Clear pinned patient"
                  onClick={() => setPinnedPatient(null)}>✕</button>
        </div>
      )}
    </div>
  );

  return { pinnedPatient, view };
}

// ---------------------------------------------------------------------------
// Draft view
// ---------------------------------------------------------------------------
function DraftView({ templates, onForbidden }) {
  const [busy, setBusy] = useState(false);
  const { pinnedPatient, view: patientView } = usePatientPin(busy);
  const [templateId, setTemplateId] = useState('');
  const [manualValues, setManualValues] = useState({});
  const [instruction, setInstruction] = useState('');
  const [subject, setSubject] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientFax, setRecipientFax] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [body, setBody] = useState('');
  const [phase, setPhase] = useState('compose'); // compose | review
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [preview, setPreview] = useState(null);   // { url, recipientName, fax }
  const [result, setResult] = useState(null);

  const template = templates.find((t) => t.id === templateId) || null;
  const manualFields = (template?.mergeFields || []).filter((f) => f.source === 'manual');

  function selectTemplate(id) {
    setTemplateId(id);
    setManualValues({});
    const t = templates.find((x) => x.id === id);
    if (t && !subject) setSubject(t.name);
  }

  async function generate(revise) {
    if (!pinnedPatient) { setErrors(['Pin a patient first.']); return; }
    setBusy(true); setErrors([]); setWarnings([]);
    try {
      const r = await ccmApi.draftProviderLetter({
        elationPatientId: pinnedPatient.elationId,
        templateId: templateId || null,
        instruction,
        manualValues,
        currentBody: revise ? body : '',
        mode: revise ? 'revise' : 'draft',
      });
      setBody(r.body || '');
      setWarnings(r.warnings || []);
      setPhase('review');
    } catch (e) {
      if (e && e.status === 403) { onForbidden(e); return; }
      setErrors(errorLines(e, 'Drafting failed.'));
    } finally { setBusy(false); }
  }

  async function openPreview() {
    setBusy(true); setErrors([]);
    try {
      const r = await ccmApi.previewProviderLetter({
        elationPatientId: pinnedPatient.elationId,
        subject,
        bodyText: body,
        recipient: { name: recipientName.trim(), fax: recipientFax.trim(), email: recipientEmail.trim() },
      });
      const url = base64PdfUrl(r.pdfBase64);
      if (!url) { setErrors(['The preview PDF could not be rendered.']); return; }
      setPreview({ url, recipientName: r.recipientName, fax: r.fax, warnings: r.warnings || [] });
    } catch (e) {
      if (e && e.status === 403) { onForbidden(e); return; }
      setErrors(errorLines(e, 'Preview failed.'));
    } finally { setBusy(false); }
  }

  function closePreview() {
    if (preview && preview.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  }

  async function send(action) {
    setBusy(true); setErrors([]);
    try {
      const r = await ccmApi.sendProviderLetter({
        elationPatientId: pinnedPatient.elationId,
        subject,
        bodyText: body,
        recipient: { name: recipientName.trim(), fax: recipientFax.trim(), email: recipientEmail.trim() },
        action,
      });
      closePreview();
      setResult(r);
    } catch (e) {
      if (e && e.status === 403) { onForbidden(e); return; }
      setErrors(errorLines(e, 'Send failed.'));
    } finally { setBusy(false); }
  }

  if (result) {
    return (
      <div className="laborders-success">
        <h3>{result.sent ? `Sent to ${result.recipient || recipientName}${result.channel ? ` via ${result.channel}` : ''}.` : 'Saved as an unsigned draft in Elation.'}</h3>
        <p className="muted">{result.sent
          ? 'The letter was signed, filed to the chart, and transmitted.'
          : 'Open the patient’s chart in Elation to review, sign, and send it.'}</p>
        {(result.warnings || []).map((w, i) => <p key={i} className="muted">⚠ {w}</p>)}
        <button className="btn" onClick={() => { setResult(null); setPhase('compose'); setBody(''); setInstruction(''); }}>Draft another</button>
      </div>
    );
  }

  return (
    <div className="letters-draft">
      {patientView}

      {phase === 'compose' && (
        <>
          <div className="field">
            <div className="field-label">Template</div>
            <select value={templateId} onChange={(e) => selectTemplate(e.target.value)} disabled={busy}>
              <option value="">Blank / one-time letter</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {template?.description ? <div className="muted">{template.description}</div> : null}
          </div>

          {manualFields.length > 0 && (
            <div className="field">
              <div className="field-label">Fill in</div>
              {manualFields.map((f) => (
                <div key={f.key} className="letters-manual-field">
                  <label>{f.label || f.key}</label>
                  <input type="text" value={manualValues[f.key] || ''} placeholder={f.default || ''}
                         onChange={(e) => setManualValues((m) => ({ ...m, [f.key]: e.target.value }))} disabled={busy} />
                </div>
              ))}
            </div>
          )}

          <div className="field">
            <div className="field-label">What should this letter say?</div>
            <textarea rows={4} value={instruction} onChange={(e) => setInstruction(e.target.value)} disabled={busy}
                      placeholder="Describe the request in plain words — the assistant grounds it in this patient's chart." />
          </div>

          <button className="btn btn-primary" disabled={busy || !pinnedPatient || (!instruction.trim() && !templateId)}
                  onClick={() => generate(false)}>
            {busy ? 'Drafting…' : 'Draft with AI'}
          </button>
        </>
      )}

      {phase === 'review' && (
        <>
          <div className="field">
            <div className="field-label">Recipient</div>
            <input type="text" value={recipientName} placeholder="Name (e.g. Quest Mobile Phlebotomy)"
                   onChange={(e) => setRecipientName(e.target.value)} disabled={busy} />
            <input type="text" value={recipientFax} placeholder="Fax number"
                   onChange={(e) => setRecipientFax(e.target.value)} disabled={busy} />
            <input type="email" value={recipientEmail} placeholder="Email (optional, if no fax)"
                   onChange={(e) => setRecipientEmail(e.target.value)} disabled={busy} />
          </div>
          <div className="field">
            <div className="field-label">Subject</div>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={busy} />
          </div>
          <div className="field">
            <div className="field-label">Letter body</div>
            <textarea rows={14} value={body} onChange={(e) => setBody(e.target.value)} disabled={busy} />
            {warnings.map((w, i) => <div key={i} className="muted">⚠ {w}</div>)}
            <button className="btn" disabled={busy || !instruction.trim()} onClick={() => generate(true)}>
              {busy ? 'Revising…' : 'Revise with AI'}
            </button>
          </div>
          <div className="letters-actions">
            <button className="btn" onClick={() => setPhase('compose')} disabled={busy}>Back</button>
            <button className="btn btn-primary" disabled={busy || !body.trim() || !recipientName.trim()} onClick={openPreview}>
              Preview &amp; send
            </button>
          </div>
        </>
      )}

      {errors.length > 0 && (
        <div className="form-errors">{errors.map((l, i) => <div key={i}>⚠ {l}</div>)}</div>
      )}

      {preview && (
        <div className="modal-overlay" onClick={closePreview}>
          <div className="modal letters-preview" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Review the letter</h3>
              <button className="modal-close" onClick={closePreview}>✕</button>
            </div>
            <iframe title="Letter preview" src={preview.url} className="letters-preview-frame" />
            <div className="letters-preview-footer">
              <div className="muted">
                Recipient: {preview.recipientName || recipientName}
                {preview.fax ? ` · fax ${preview.fax}` : recipientEmail ? ` · ${recipientEmail}` : ''} — for {pinnedPatient?.firstName} {pinnedPatient?.lastName}
              </div>
              <div className="muted">
                Save draft leaves an unsigned letter in Elation. Sign &amp; send signs it and faxes/emails it now — an outside party gets it immediately.
              </div>
              {(preview.warnings || []).map((w, i) => <div key={i} className="muted">⚠ {w}</div>)}
              {errors.map((l, i) => <div key={i} className="form-errors">⚠ {l}</div>)}
              <div className="letters-actions">
                <button className="btn" disabled={busy} onClick={() => send('draft')}>Save draft</button>
                <button className="btn btn-primary" disabled={busy} onClick={() => send('send')}>
                  {busy ? 'Working…' : 'Sign & send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Templates builder
// ---------------------------------------------------------------------------
function emptyField() { return { key: '', label: '', source: 'manual', default: '' }; }

function TemplateEditor({ editing, onSave, onCancel, saving, error }) {
  const [name, setName] = useState(editing?.name || '');
  const [description, setDescription] = useState(editing?.description || '');
  const [instructions, setInstructions] = useState(editing?.instructions || '');
  const [bodyScaffold, setBodyScaffold] = useState(editing?.bodyScaffold || '');
  const [fields, setFields] = useState(editing?.mergeFields?.length ? editing.mergeFields.map((f) => ({ ...f, default: f.default || '' })) : []);

  function updateField(i, patch) { setFields((fs) => fs.map((f, idx) => (idx === i ? { ...f, ...patch } : f))); }

  function submit() {
    const cleaned = fields
      .map((f) => ({ key: String(f.key || '').toUpperCase().replace(/[^A-Z0-9_]/g, ''), label: f.label, source: f.source === 'manual' ? 'manual' : 'elation', default: f.default || undefined }))
      .filter((f) => f.key);
    onSave({ name: name.trim(), kind: 'prose', description, instructions, mergeFields: cleaned, bodyScaffold });
  }

  return (
    <div className="letters-template-editor">
      <div className="field"><div className="field-label">Name</div>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Homebound status letter" /></div>
      <div className="field"><div className="field-label">Description (optional)</div>
        <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} /></div>
      <div className="field"><div className="field-label">Drafting instructions</div>
        <textarea rows={4} value={instructions} onChange={(e) => setInstructions(e.target.value)}
                  placeholder="Guidance the assistant follows every time. Reference merge fields as [TOKEN]." /></div>

      <div className="field">
        <div className="field-label">Merge fields</div>
        <div className="muted">Elation fields fill from the chart; manual fields are asked when drafting.</div>
        {fields.map((f, i) => (
          <div key={i} className="letters-field-row">
            <input type="text" value={f.key} placeholder="KEY"
                   onChange={(e) => updateField(i, { key: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })} />
            <input type="text" value={f.label} placeholder="Label" onChange={(e) => updateField(i, { label: e.target.value })} />
            <select value={f.source} onChange={(e) => updateField(i, { source: e.target.value })}>
              <option value="elation">Elation</option>
              <option value="manual">Manual</option>
            </select>
            {f.source === 'manual'
              ? <input type="text" value={f.default} placeholder="Default (optional)" onChange={(e) => updateField(i, { default: e.target.value })} />
              : <span />}
            <button className="btn btn-small" onClick={() => setFields((fs) => fs.filter((_, idx) => idx !== i))}>Remove</button>
          </div>
        ))}
        <button className="btn btn-small" onClick={() => setFields((fs) => [...fs, emptyField()])}>Add field</button>
      </div>

      <div className="field"><div className="field-label">Body scaffold (optional)</div>
        <textarea rows={4} value={bodyScaffold} onChange={(e) => setBodyScaffold(e.target.value)}
                  placeholder="A skeleton the assistant fills in — put [TOKEN]s where values go." /></div>

      {error ? <div className="form-errors">⚠ {error}</div> : null}
      <div className="letters-actions">
        <button className="btn" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving || !name.trim()}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  );
}

function TemplatesView({ templates, reload }) {
  const [editing, setEditing] = useState(null); // 'new' | template | null
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function save(input) {
    setSaving(true); setError(null);
    try {
      if (editing && editing !== 'new') await ccmApi.updateLetterTemplate(editing.id, input);
      else await ccmApi.createLetterTemplate(input);
      setEditing(null);
      await reload();
    } catch (e) { setError((e && e.message) || 'Save failed.'); }
    finally { setSaving(false); }
  }

  async function setStatus(t, status) {
    try { await ccmApi.setLetterTemplateStatus(t.id, status); await reload(); } catch (e) { setError((e && e.message) || 'Update failed.'); }
  }
  async function remove(t) {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try { await ccmApi.deleteLetterTemplate(t.id); await reload(); } catch (e) { setError((e && e.message) || 'Delete failed.'); }
  }

  if (editing) {
    return <TemplateEditor editing={editing === 'new' ? null : editing} onSave={save}
                           onCancel={() => setEditing(null)} saving={saving} error={error} />;
  }

  return (
    <div className="letters-templates">
      <div className="letters-templates-head">
        <p className="muted">Build reusable letters your team can fill for any patient.</p>
        <button className="btn btn-primary" onClick={() => setEditing('new')}>New template</button>
      </div>
      {error ? <div className="form-errors">⚠ {error}</div> : null}
      {templates.length === 0 ? (
        <p className="muted">No templates yet. Create your first, or draft a one-time letter from the Draft tab.</p>
      ) : (
        <ul className="letters-template-list">
          {templates.map((t) => (
            <li key={t.id} className="letters-template-item">
              <div className="letters-template-main" onClick={() => setEditing(t)}>
                <strong>{t.name}</strong>
                <span className={`badge badge-${t.status}`}>{t.status}</span>
                {t.description ? <div className="muted">{t.description}</div> : null}
                {t.mergeFields?.length ? <div className="muted">{t.mergeFields.length} merge field{t.mergeFields.length === 1 ? '' : 's'}</div> : null}
              </div>
              <div className="letters-template-actions">
                <button className="btn btn-small" onClick={() => setEditing(t)}>Edit</button>
                {t.status === 'draft'
                  ? <button className="btn btn-small" onClick={() => setStatus(t, 'active')}>Publish</button>
                  : t.status === 'active'
                    ? <button className="btn btn-small" onClick={() => setStatus(t, 'retired')}>Retire</button>
                    : null}
                <button className="btn btn-small" onClick={() => remove(t)}>Delete</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export function LettersPage() {
  const [view, setView] = useState('draft'); // draft | templates
  const [templates, setTemplates] = useState([]);
  const [state, setState] = useState('loading'); // loading | ready | dormant | forbidden | error
  const [errorMsg, setErrorMsg] = useState('');

  const reload = useCallback(async () => {
    try {
      const data = await ccmApi.listLetterTemplates(true);
      setTemplates((data && data.templates) || []);
      setState('ready');
    } catch (e) {
      if (e && e.status === 404) { setState('dormant'); return; }
      if (e && e.status === 403) { setState('forbidden'); setErrorMsg((e && e.message) || ''); return; }
      setState('error'); setErrorMsg((e && e.message) || 'Could not load templates.');
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  function onForbidden(e) { setState('forbidden'); setErrorMsg((e && e.message) || ''); }

  if (state === 'loading') return <div className="page-pad">Loading letters…</div>;
  if (state === 'dormant') return <div className="page-pad muted">Provider letters aren’t enabled for this practice yet. Ask your administrator to turn them on.</div>;
  if (state === 'forbidden') return <div className="page-pad muted">{errorMsg || 'You do not have access to provider letters.'}</div>;
  if (state === 'error') return <div className="page-pad"><div className="form-errors">⚠ {errorMsg}</div><button className="btn" onClick={reload}>Retry</button></div>;

  const activeTemplates = templates.filter((t) => t.status === 'active');

  return (
    <div className="letters-page page-pad">
      <div className="letters-tabs">
        <button className={`letters-tab ${view === 'draft' ? 'active' : ''}`} onClick={() => setView('draft')}>Draft a letter</button>
        <button className={`letters-tab ${view === 'templates' ? 'active' : ''}`} onClick={() => setView('templates')}>Templates</button>
      </div>
      {view === 'draft'
        ? <DraftView templates={activeTemplates} onForbidden={onForbidden} />
        : <TemplatesView templates={templates} reload={reload} />}
    </div>
  );
}
